import React from "react";
import { ArrowDown } from "lucide-react";
import { Link } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";

const HERO_IMAGE = "/media/luft-hero-editorial.jpg";
const HERO_ACCENT = "/media/luft-mat-yoga-studio.jpg";

export default function Hero() {
  const scrollNext = () => {
    document.getElementById("filosofia")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="hero"
      className="relative w-full bg-[var(--luft-bg)] overflow-hidden"
      data-testid="hero-section"
    >
      {/* Soft sky ambient */}
      <div
        className="pointer-events-none absolute -top-40 -right-40 w-[70vw] h-[70vw] rounded-full opacity-[0.35]"
        style={{
          background:
            "radial-gradient(circle, rgba(181,199,211,0.7) 0%, transparent 65%)",
        }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -bottom-40 -left-24 w-[50vw] h-[50vw] rounded-full opacity-[0.22]"
        style={{
          background:
            "radial-gradient(circle, rgba(82,96,254,0.28) 0%, transparent 68%)",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16 pt-32 sm:pt-36 lg:pt-40 pb-20 sm:pb-24 lg:pb-28">
        {/* Meta strip */}
        <div className="flex items-end justify-between mb-10 lg:mb-14">
          <p className="eyebrow" data-testid="hero-eyebrow">
            Boutique Pilates Studio · Est. mmxxii
          </p>
          <p className="subtitle-italic hidden sm:inline-block text-[var(--luft-ink-soft)]">
            Entroncamento · Portugal
          </p>
        </div>

        {/* Main composition: image left, text overlapping right */}
        <div className="relative grid grid-cols-12 gap-y-10 lg:gap-x-10">
          {/* Primary image */}
          <div className="col-span-12 lg:col-span-8 relative">
            <div
              className="relative overflow-hidden shadow-[0_28px_80px_-40px_rgba(30,33,38,0.4)]"
              style={{ borderRadius: "var(--radius-hero)" }}
            >
              <img
                src={HERO_IMAGE}
                alt="Estúdio Lu.ft em luz natural"
                loading="eager"
                fetchpriority="high"
                className="w-full h-[64vh] min-h-[440px] lg:h-[78vh] lg:min-h-[600px] object-cover object-[30%_center]"
                data-testid="hero-image"
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, transparent 0%, transparent 55%, rgba(30,33,38,0.18) 100%)",
                }}
              />

              {/* Headline overlaid on image, bottom left */}
              <div className="absolute bottom-8 sm:bottom-10 lg:bottom-14 left-6 sm:left-10 right-6 sm:right-16">
                <h1
                  className="editorial-heading text-[var(--luft-ivory)] text-[clamp(2.2rem,6vw,5.6rem)] leading-[0.94] drop-shadow-[0_4px_28px_rgba(30,33,38,0.25)]"
                  data-testid="hero-title"
                >
                  Mover-se <em className="editorial-italic">bem</em>.<br />
                  Sentir <em className="editorial-italic">mais</em>.
                </h1>
              </div>

              {/* Small floating tag */}
              <span
                className="absolute top-6 left-6 sm:top-8 sm:left-8 px-3 py-1.5 rounded-full backdrop-blur-md bg-white/25 text-[var(--luft-ivory)]"
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: "0.58rem",
                  letterSpacing: "0.32em",
                  textTransform: "uppercase",
                }}
              >
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full mr-2 align-middle"
                  style={{ backgroundColor: "var(--luft-yellow)" }}
                  aria-hidden="true"
                />
                Pilates · Movimento consciente
              </span>
            </div>
          </div>

          {/* Right column — accent image + copy */}
          <div className="col-span-12 lg:col-span-4 lg:pl-6 lg:pt-16 flex flex-col justify-between gap-10">
            {/* Small vertical accent image (desktop) */}
            <div
              className="hidden lg:block relative overflow-hidden"
              style={{ borderRadius: "var(--radius-organic)" }}
            >
              <img
                src={HERO_ACCENT}
                alt="Detalhe do estúdio"
                loading="eager"
                className="w-full h-[280px] object-cover"
                style={{ filter: "brightness(1.03) saturate(1.02)" }}
              />
            </div>

            {/* Copy + CTAs */}
            <div className="space-y-8 lg:pl-2">
              <p className="font-body font-light text-[var(--luft-ink-soft)] text-[1rem] leading-[1.75] max-w-md">
                Um estúdio íntimo em Entroncamento, dedicado ao movimento
                consciente, à luz natural e à atenção individual.
              </p>

              <div className="flex flex-col sm:flex-row lg:flex-col sm:items-center lg:items-start gap-5 lg:gap-6">
                <Link
                  to="/reservar"
                  className="btn-ink self-start"
                  data-testid="hero-reservar-btn"
                  onClick={() => trackEvent("reservar_click", { cta_location: "hero" })}
                >
                  Reservar aula
                </Link>
                <button
                  onClick={scrollNext}
                  className="link-underline text-[var(--luft-ink)] text-[0.72rem] uppercase tracking-[0.28em] self-start"
                  data-testid="hero-explore-btn"
                >
                  Descobrir o estúdio
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll cue */}
        <button
          onClick={scrollNext}
          className="hidden lg:flex mt-16 items-center gap-3 text-[var(--luft-ink-soft)]"
          aria-label="Continuar"
          data-testid="hero-scroll-cue"
        >
          <span className="eyebrow">Scroll · continuar</span>
          <ArrowDown size={14} strokeWidth={1} className="animate-bounce" />
        </button>
      </div>
    </section>
  );
}
